// WorkspaceEdit.js — rearranging the tabs and the Apps tray, home-screen
// style. Right-click any tab or tile for Pin/Unpin, Move and Edit layout;
// Edit layout makes everything jiggle and drag — within the tabs, within the
// tray, and between them (into the tabs pins, into the tray unpins). Nothing
// drags outside edit mode.
import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { APPS_ID, LOCKED } from '../../utils/workspace/workspaceLayout';

// dataTransfer can't be read during dragover, so the dragged id lives here.
let dragging = null;
export const dragState = {
  get: () => dragging,
  start: (id) => {
    dragging = id;
  },
  end: () => {
    dragging = null;
  },
};

const DRAG_TYPE = 'application/x-comprehend-workspace-item';

/** Start a drag, the same for tabs and tiles. */
export function beginDrag(e, id, workspace) {
  dragState.start(id);
  workspace?.setDragging(id);
  e.dataTransfer.effectAllowed = 'move';
  try {
    e.dataTransfer.setData(DRAG_TYPE, id);
  } catch (err) {
    /* the shared drag state is enough */
  }
}

const STYLE_ID = 'cpt-workspace-edit-style';
/** The jiggle, once per page. Two phases so neighbours don't move in lockstep. */
export function ensureJiggleStyle() {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = STYLE_ID;
  style.textContent =
    '@keyframes cpt-jiggle{0%{transform:rotate(-1.4deg)}50%{transform:rotate(1.4deg)}100%{transform:rotate(-1.4deg)}}' +
    '.cpt-jiggle{animation:cpt-jiggle .28s ease-in-out infinite;transform-origin:50% 60%;cursor:grab}' +
    '.cpt-jiggle.cpt-jiggle-b{animation-delay:-.14s}' +
    '@media (prefers-reduced-motion: reduce){.cpt-jiggle{animation:none;outline:1px dashed #adb5bd;outline-offset:2px}}' +
    // Edit mode: the tab bar reads as a place things go, and lights up while
    // a tray tile is on its way.
    '.cpt-tabs-editing{outline:1.5px dashed #c3cadb;outline-offset:-3px;border-radius:10px;transition:background .12s,outline-color .12s}' +
    '.cpt-tabs-dropping{outline:2px dashed #5e72e4;background:rgba(94,114,228,.06)}';
  document.head.appendChild(style);
}

/** The −/+ badge an item wears in edit mode. */
export function EditBadge({ kind, label, onClick, small = false }) {
  return (
    <span
      role="button"
      tabIndex={0}
      aria-label={label}
      title={label}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onClick();
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          e.stopPropagation();
          onClick();
        }
      }}
      style={{
        position: 'absolute',
        // Small (compact tabs): clear of an 18px icon rather than on top of it.
        top: small ? -9 : -6,
        left: small ? -10 : -6,
        width: small ? 13 : 16,
        height: small ? 13 : 16,
        borderRadius: '50%',
        background: kind === 'remove' ? '#8898aa' : '#5e72e4',
        color: '#fff',
        fontSize: small ? 11 : 13,
        fontWeight: 700,
        lineHeight: small ? '12px' : '15px',
        textAlign: 'center',
        boxShadow: '0 1px 2px rgba(0,0,0,.25)',
        cursor: 'pointer',
        zIndex: 2,
        animation: 'none',
      }}
    >
      {kind === 'remove' ? '−' : '+'}
    </span>
  );
}

/** Which half of the element the pointer is over, along its layout axis. */
export const sideOf = (e, axis = 'x') => {
  const rect = e.currentTarget.getBoundingClientRect();
  return axis === 'x' ? (e.clientX > rect.left + rect.width / 2 ? 'after' : 'before') : e.clientY > rect.top + rect.height / 2 ? 'after' : 'before';
};

/** The right-click menu for one item. */
export function WorkspaceMenu({ workspace }) {
  const menu = workspace.menu;
  useEffect(() => {
    if (!menu) return undefined;
    const close = () => workspace.closeMenu();
    const onKey = (e) => e.key === 'Escape' && close();
    window.addEventListener('mousedown', close);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', close);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
      window.removeEventListener('keydown', onKey);
    };
  }, [menu, workspace]);
  if (!menu || typeof document === 'undefined') return null;
  // The Apps tab is not an item: its menu only has Edit layout.
  const item = menu.id === APPS_ID ? { id: APPS_ID, label: 'Apps' } : workspace.byId[menu.id];
  if (!item) return null;
  const isApps = item.id === APPS_ID;
  const locked = isApps || LOCKED.includes(item.id);
  const pinned = !isApps && workspace.isPinned(item.id);
  const group = isApps ? [] : (pinned ? workspace.pinned : workspace.unpinned).map((i) => i.id);
  const at = group.indexOf(item.id);
  const run = (fn) => (e) => {
    e.stopPropagation();
    fn();
    workspace.closeMenu();
  };
  const entries = [
    !locked && { label: pinned ? 'Unpin — move to Apps' : 'Pin to tabs & rail', fn: () => workspace.setPinned(item.id, !pinned) },
    !isApps && at > 0 && { label: pinned ? 'Move left' : 'Move earlier', fn: () => workspace.nudge(item.id, -1, group) },
    !isApps && at < group.length - 1 && { label: pinned ? 'Move right' : 'Move later', fn: () => workspace.nudge(item.id, 1, group) },
    !isApps && { divider: true },
    { label: workspace.editing ? 'Done editing' : 'Edit layout…', fn: () => workspace.setEditing(!workspace.editing) },
  ].filter(Boolean);
  const x = Math.min(menu.x, window.innerWidth - 200);
  const y = Math.min(menu.y, window.innerHeight - 40 * entries.length);
  return createPortal(
    <div
      role="menu"
      aria-label={`${item.label} options`}
      data-workspace-menu
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        left: x,
        top: y,
        zIndex: 2000,
        minWidth: 190,
        background: '#fff',
        borderRadius: 8,
        boxShadow: '0 6px 24px rgba(15,23,42,.18), 0 0 0 1px rgba(15,23,42,.06)',
        padding: 4,
        fontSize: 13,
      }}
    >
      <div style={{ padding: '4px 10px 6px', fontSize: 11, fontWeight: 700, color: '#8898aa', textTransform: 'uppercase', letterSpacing: '.04em' }}>{item.label}</div>
      {entries.map((entry, i) =>
        entry.divider ? (
          <div key={`d${i}`} style={{ height: 1, background: '#e9ecef', margin: '4px 0' }} />
        ) : (
          <button
            key={entry.label}
            type="button"
            role="menuitem"
            onClick={run(entry.fn)}
            style={{ display: 'block', width: '100%', textAlign: 'left', border: 0, background: 'transparent', padding: '6px 10px', borderRadius: 5, color: '#32325d', cursor: 'pointer' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f1f3f9';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            {entry.label}
          </button>
        ),
      )}
    </div>,
    document.body,
  );
}
